import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { UserAvatar } from '../components/shared/UserAvatar';
import { Download, Mail } from 'lucide-react';
import { PerformanceSendSheet } from '../components/performance/PerformanceSendSheet';
import { downloadPdf } from '../utils/pdfDownload';
import type { PerformancePeriod } from '../types/performance';
import { PERFORMANCE_PERIODS } from '../types/performance';

type Period = PerformancePeriod;

interface LeaderboardEntry {
  user_id: string;
  full_name: string;
  job_title: string | null;
  photo_url: string | null;
  visits: number;
  placements: number;
  score: number;
  rank: number;
}

const PERIODS = PERFORMANCE_PERIODS;

const Leaderboard: React.FC = () => {
  const { user, team, isTeamAdmin, isSuperAdmin } = useAuth();
  const [period, setPeriod] = useState<Period>('month');
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const canExport = isTeamAdmin || isSuperAdmin;

  useEffect(() => {
    if (!team?.id) {
      setEntries([]);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/visits/leaderboard?period=${period}`);
        setEntries(res.data?.entries || []);
      } catch {
        toast.error('Failed to load leaderboard');
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period, team?.id]);

  const onDownloadTeam = async () => {
    try {
      await downloadPdf(
        `/performance/pdf?scope=team&period=${period}`,
        `performance-team-${period}.pdf`
      );
    } catch {
      toast.error('Could not download overview');
    }
  };

  if (!team) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center text-gray-500">
        Leaderboard requires a team. Super-admins without a team cannot view the scoreboard.
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
            Team
          </p>
          <h1 className="text-base font-black text-gray-900">Leaderboard</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Visits + 5 × placements
          </p>
        </div>
        {canExport ? (
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => void onDownloadTeam()}
              className="min-h-[36px] px-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600"
            >
              <Download size={14} className="inline mr-1" />
              PDF
            </button>
            <button
              type="button"
              onClick={() => setSendOpen(true)}
              className="min-h-[36px] px-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600"
            >
              <Mail size={14} className="inline mr-1" />
              Send
            </button>
          </div>
        ) : null}
      </div>
      <div className="flex rounded-xl border border-gray-200 overflow-hidden bg-white">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPeriod(p.id)}
              className={`flex-1 min-h-[40px] px-2 text-xs font-bold ${
                period === p.id ? 'bg-gray-900 text-white' : 'text-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No team members yet.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {entries.map((entry) => {
              const isMe = entry.user_id === user?.id;
              return (
                <li key={entry.user_id}>
                  <Link
                    to={`/leaderboard/${entry.user_id}`}
                    className={`px-4 py-3 flex items-center gap-3 ${
                      isMe ? 'bg-gray-50' : ''
                    }`}
                  >
                  <span
                    className={`w-8 text-center font-bold text-sm ${
                      entry.rank === 1
                        ? 'text-amber-500'
                        : entry.rank === 2
                          ? 'text-gray-400'
                          : entry.rank === 3
                            ? 'text-amber-700'
                            : 'text-gray-500'
                    }`}
                  >
                    #{entry.rank}
                  </span>
                  <UserAvatar
                    userId={entry.user_id}
                    fullName={entry.full_name}
                    photoUrl={entry.photo_url}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {entry.full_name}
                      {isMe && (
                        <span className="ml-2 text-xs font-medium text-brand-600">
                          You
                        </span>
                      )}
                    </p>
                    {entry.job_title && (
                      <p className="text-xs text-gray-500 truncate">
                        {entry.job_title}
                      </p>
                    )}
                  </div>
                  <div className="text-right text-xs text-gray-500 space-y-0.5 hidden sm:block">
                    <div>{entry.visits} visits</div>
                    <div>{entry.placements} placements</div>
                  </div>
                  <div className="text-right min-w-[3.5rem]">
                    <div className="text-lg font-bold text-gray-900">
                      {entry.score}
                    </div>
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">
                      score
                    </div>
                  </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-center text-xs text-gray-400">
        Update your photo on{' '}
        <Link to="/profile" className="font-bold text-gray-600">
          your profile
        </Link>
        .
      </p>
      {canExport ? (
        <PerformanceSendSheet
          open={sendOpen}
          onOpenChange={setSendOpen}
          scope="team"
          period={period}
        />
      ) : null}
    </div>
  );
};

export default Leaderboard;
